<script setup>
import { ref, watch } from 'vue'
import CommerceContentTarget from './CommerceContentTarget.vue'
import ProductPickerModal from '@/components/ProductPickerModal.vue'
import ImageUploader from '@/components/ImageUploader.vue'
import { productService } from '@/api/modules/products.js'
import apiClient from '@/api/axios.js'
import { useCommerceContentModel } from '@/composables/useCommerceContentModel.js'
const props = defineProps({ modelValue: { type: Object, required: true } })
const emit = defineEmits(['update:modelValue'])
const form = useCommerceContentModel(props, emit)
const definitions = [
  { id: 'hero', type: 'banner', title: '首頁主視覺', slot: 'hero' },
  { id: 'categories', type: 'categories', title: '商品分類' },
  { id: 'ads', type: 'banner', title: '精選活動', slot: 'ads' },
  { id: 'weekly', type: 'products', title: '週間好福利' },
  { id: 'offers', type: 'products', title: '優惠專區' },
  { id: 'campaign', type: 'banner', title: '活動橫幅', slot: 'campaign' },
  { id: 'recommendations', type: 'products', title: '你可能會喜歡' },
  { id: 'editorial', type: 'products', title: '編輯嚴選' },
  { id: 'brands', type: 'brands', title: '品牌快速導覽' }
]
const picking = ref(null)
const productNames = ref({})
watch(() => form.value.sections, async sections => {
  const ids = [...new Set((sections || []).flatMap(s => s.productIds || []))]
  await Promise.allSettled(ids.filter(id => !productNames.value[id]).map(async id => {
    const res = await productService.getProduct(id)
    productNames.value[id] = res.data?.name || '已選商品'
  }))
}, { immediate: true, deep: true })
function add(definition) {
  form.value.sections.push({ ...definition, enabled: true, subtitle: '', href: '', limit: 8, sourceType: 'products', sourceRef: '', productIds: [], categorySlugs: [], categoryImages: {}, brands: [] })
}
function move(list, index, direction) {
  const next = index + direction
  if (next < 0 || next >= list.length) return
  list.splice(next, 0, list.splice(index, 1)[0])
}
function selectProduct({ product }) {
  if (!product || !picking.value) return
  if (!picking.value.productIds.includes(product.id) && picking.value.productIds.length < 24) picking.value.productIds.push(product.id)
  productNames.value[product.id] = product.name
  picking.value = null
}
async function uploadImage(file) {
  const data = new FormData(); data.append('file', file)
  return apiClient.post('/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } })
}
</script>
<template>
  <div class="settings-card home-editor">
    <div class="settings-card-header"><h2>首頁內容</h2></div>
    <div class="form-body">
      <fieldset>
        <legend>全站公告</legend>
        <label><input v-model="form.announcement.enabled" type="checkbox" class="toggle-input" /> 顯示公告</label>
        <label>公告文字<input v-model="form.announcement.text" class="form-control" maxlength="1000" /></label>
        <label>公告連結<input v-model="form.announcement.href" class="form-control" placeholder="/page/news 或 https://..." /></label>
        <p class="form-hint">顯示在全站頁首。優惠文字由營運確認與實際規則一致；留空不顯示。</p>
      </fieldset>
      <p class="form-hint">保留目前版型。區塊依清單順序呈現，沒有內容的區塊會自動隱藏；儲存後重新載入前台即可看到變更。</p>
      <article v-for="(section, index) in form.sections" :key="section.id" class="content-section">
        <div class="section-toolbar">
          <strong>{{ definitions.find(d => d.id === section.id)?.title || section.title }}</strong>
          <label><input v-model="section.enabled" type="checkbox" class="toggle-input" /> 顯示</label>
          <button type="button" class="btn btn-secondary btn-sm" :disabled="!index" @click="move(form.sections, index, -1)">上移</button>
          <button type="button" class="btn btn-secondary btn-sm" :disabled="index === form.sections.length - 1" @click="move(form.sections, index, 1)">下移</button>
          <button type="button" class="btn btn-secondary btn-sm" @click="form.sections.splice(index, 1)">移除區塊</button>
        </div>
        <label>標題<input v-model="section.title" class="form-control" /></label>
        <label>副標<textarea v-model="section.subtitle" class="form-control" rows="2" /></label>
        <label>查看更多連結<input v-model="section.href" class="form-control" placeholder="/products 或 /page/..." /></label>
        <template v-if="section.type === 'products'">
          <label>商品來源<select v-model="section.sourceType" class="form-control" @change="section.sourceRef = ''"><option value="products">指定商品</option><option value="hall">館別</option><option value="category">商品分類</option></select></label>
          <CommerceContentTarget v-if="section.sourceType !== 'products'" v-model="section.sourceRef" :type="section.sourceType" />
          <template v-else>
            <div v-for="(id, pi) in section.productIds" :key="id" class="item-row">
              <span>{{ productNames[id] || '載入商品名稱中…' }}</span>
              <button type="button" class="btn btn-secondary btn-sm" :disabled="!pi" @click="move(section.productIds, pi, -1)">上移</button>
              <button type="button" class="btn btn-secondary btn-sm" :disabled="pi === section.productIds.length - 1" @click="move(section.productIds, pi, 1)">下移</button>
              <button type="button" class="btn btn-secondary btn-sm" @click="section.productIds.splice(pi, 1)">移除</button>
            </div>
            <button type="button" class="btn btn-secondary" :disabled="section.productIds.length >= 24" @click="picking = section">選擇商品</button>
          </template>
          <label>展示數量<input v-model.number="section.limit" type="number" min="1" max="24" class="form-control" /></label>
        </template>
        <template v-if="section.type === 'categories'">
          <div v-for="(slug, ci) in section.categorySlugs" :key="ci" class="item-row"><CommerceContentTarget v-model="section.categorySlugs[ci]" type="category" /><button type="button" class="btn btn-secondary btn-sm" :disabled="!ci" @click="move(section.categorySlugs, ci, -1)">上移</button><button type="button" class="btn btn-secondary btn-sm" :disabled="ci === section.categorySlugs.length - 1" @click="move(section.categorySlugs, ci, 1)">下移</button><button type="button" class="btn btn-secondary btn-sm" @click="section.categorySlugs.splice(ci, 1)">移除</button><ImageUploader v-if="slug && section.categoryImages" v-model="section.categoryImages[slug]" :uploadFn="uploadImage" shape="card" hint="分類捷徑圖片（選填）" /></div>
          <button type="button" class="btn btn-secondary" @click="section.categorySlugs.push('')">新增分類捷徑</button>
        </template>
        <p v-if="section.type === 'banner'" class="form-hint">請在 <RouterLink to="/client/commerce/banners">Banner 管理</RouterLink> 選擇「首頁」及「{{ section.slot === 'hero' ? '主視覺' : section.slot === 'ads' ? '廣告圖組' : '活動橫幅' }}」插槽上傳桌機／手機圖片，排序與有效期間沿用 Banner 設定。</p>
        <template v-if="section.type === 'brands'">
          <div v-for="(brand, bi) in section.brands" :key="bi" class="brand-row">
            <label>品牌名稱<input v-model="brand.name" class="form-control" /></label>
            <ImageUploader v-model="brand.imageUrl" :uploadFn="uploadImage" shape="card" />
            <label>品牌連結<input v-model="brand.href" class="form-control" /></label>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="!bi" @click="move(section.brands, bi, -1)">上移</button>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="bi === section.brands.length - 1" @click="move(section.brands, bi, 1)">下移</button>
            <button type="button" class="btn btn-secondary btn-sm" @click="section.brands.splice(bi, 1)">移除品牌</button>
          </div>
          <button type="button" class="btn btn-secondary" @click="section.brands.push({ name: '', imageUrl: '', href: '' })">新增品牌</button>
        </template>
      </article>
      <div class="add-sections"><button v-for="definition in definitions.filter(d => !form.sections.some(s => s.id === d.id))" :key="definition.id" type="button" class="btn btn-secondary" @click="add(definition)">新增{{ definition.title }}</button></div>
    </div>
    <ProductPickerModal :open="!!picking" product-level-only title="選擇首頁商品" @close="picking = null" @select="selectProduct" />
  </div>
</template>
<style scoped>
.settings-card-header { display:flex; gap:16px; padding:24px; background:#f8f9ff; border-bottom:1px solid #e9ecef; }.settings-card-header h2 { font-size:18px; font-weight:600; color:#2d3748; margin:0; }.form-body { padding:24px; }.home-editor legend { font-size:15px; font-weight:600; width:auto; padding:0 8px; }.home-editor label { font-size:14px; color:#475569; }.form-hint { font-size:13px; color:#6c757d; line-height:1.6; }

.toggle-input { width: 18px; height: 18px; accent-color: #667eea; flex-shrink: 0; cursor: pointer; vertical-align: middle; }
.home-editor label { display:block; margin:12px 0; }.content-section, fieldset { border:1px solid #e2e8f0; padding:20px; margin:20px 0; border-radius:10px; }.section-toolbar,.item-row,.add-sections { display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin:12px 0; }.section-toolbar strong,.item-row span { margin-right:auto; }.item-row select { width:auto; flex:1; }.brand-row { border-top:1px solid #ddd; padding:12px 0; }.form-control { max-width:100%; }
</style>
